/**
 * The cost-accounting route table (design C3 §5.9, §6.3).
 *
 * Exported as `COST_ROUTES` and mounted by **P9** in `web/src/router/index.ts`,
 * which this packet does not own. Nothing here is lazy for the sake of it: every
 * screen is a dynamic import so a tenant whose plan omits cost accounting never
 * downloads the solver's trace renderer.
 *
 * `/cost/runs/:id` is the screen §5.9 singles out — "the run screen showing the
 * iteration trace and the flow matrix" — and it is a route rather than a drawer
 * because a controller sends the link to somebody who is going to argue with the
 * numbers.
 */

import type { RouteRecordRaw } from 'vue-router'

import { COST_PERMISSIONS } from '@/modules/cost/permissions'

export const COST_ROUTES: RouteRecordRaw[] = [
  {
    path: '/cost/pools',
    name: 'cost-pools',
    component: () => import('@/modules/cost/PoolsView.vue'),
    meta: { titleKey: 'cost.routeTitles.pools', permission: COST_PERMISSIONS.poolRead },
  },
  {
    path: '/cost/drivers',
    name: 'cost-drivers',
    component: () => import('@/modules/cost/DriversView.vue'),
    meta: { titleKey: 'cost.routeTitles.drivers', permission: COST_PERMISSIONS.driverRead },
  },
  {
    path: '/cost/drivers/:id(\\d+)/quantities',
    name: 'cost-driver-quantities',
    component: () => import('@/modules/cost/DriverQuantitiesView.vue'),
    meta: { titleKey: 'cost.routeTitles.quantities', permission: COST_PERMISSIONS.driverRead },
  },
  {
    path: '/cost/cycles',
    name: 'cost-cycles',
    component: () => import('@/modules/cost/CyclesView.vue'),
    meta: { titleKey: 'cost.routeTitles.cycles', permission: COST_PERMISSIONS.cycleRead },
  },
  {
    path: '/cost/cycles/:id(\\d+)',
    name: 'cost-cycle',
    component: () => import('@/modules/cost/CycleEditorView.vue'),
    meta: { titleKey: 'cost.routeTitles.cycle', permission: COST_PERMISSIONS.cycleRead },
  },
  {
    path: '/cost/runs',
    name: 'cost-runs',
    component: () => import('@/modules/cost/RunsView.vue'),
    meta: { titleKey: 'cost.routeTitles.runs', permission: COST_PERMISSIONS.runCalculate },
  },
  {
    path: '/cost/runs/:id(\\d+)',
    name: 'cost-run',
    component: () => import('@/modules/cost/RunDetailView.vue'),
    meta: { titleKey: 'cost.routeTitles.run', permission: COST_PERMISSIONS.reportRead },
  },
  {
    path: '/cost/absorption-rates',
    name: 'cost-absorption-rates',
    component: () => import('@/modules/cost/AbsorptionRatesView.vue'),
    meta: { titleKey: 'cost.routeTitles.rates', permission: COST_PERMISSIONS.reportRead },
  },
  // `/cost/reports` (the combined two-tab page) is retired: the two reports are
  // their own screens in `reports/routes.ts`, and the old path is a redirect in
  // `router/legacyRedirects.ts` — a route record may not share its path.
]
